import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Element } from 'react-scroll';

import theme from 'src/styles/components/AddonsConfigurator.module.scss';

import RadioAddons from './RadioAddons';
import CheckboxAddons from './CheckboxAddons';
import ConfigurableAddons from './ConfigurableAddons';
import MultiAddons from './MultiAddons';

const AddonsConfigurator = ({
    name,
    id,
    virtualId,
    maxQuantity,
    addons,
    controlType,
    selectedAddon,
    setAddonAsSelected,
    setAddonAsUnselected,
    addAddon,
    removeAddon,
    validationErrors,
    selectTranslations
}) => {
    const intl = useIntl();

    let Configurator = '';

    const handleSelectAddon = useCallback((addonId, { quantity } = {}) => {
        setAddonAsSelected(addonId, quantity);
    });

    const handleUnselectAddon = useCallback((addonId, { quantity } = {}) => {
        setAddonAsUnselected(addonId, quantity);
    });

    const handleAddAddon = useCallback((addonId) => {
        addAddon(addonId);
    });

    const handleRemoveAddon = useCallback((addonId, position) => {
        removeAddon(addonId, position);
    });

    if (controlType === 'radio') {
        Configurator = RadioAddons;
    } else if (controlType === 'checkbox') {
        Configurator = CheckboxAddons;
    } else if (controlType === 'multi') {
        Configurator = MultiAddons;
    } else {
        Configurator = ConfigurableAddons;
    }

    if (!addons) {
        return <div />;
    }

    const validationError = validationErrors.find((error) => error.groupId === virtualId);

    const selectTranslation = selectTranslations[controlType];

    return (
        <div>
            <h2 className={theme.categoryName}>
                {name}
                {' '}
                {selectTranslation && (
                    <span className={theme.categoryQuantity}>
                        {'('}
                        {intl.formatMessage({ id: `App.${selectTranslation}` }, { count: maxQuantity })}
                        {')'}
                    </span>
                )}
            </h2>
            {validationError && (
                <h4 className={theme.validationErrors}>
                    {intl.formatMessage({ id: `App.${validationError.error}` })}
                </h4>
            )}
            <div>
                <Element name={virtualId}>
                    <Configurator
                      id={id}
                      virtualId={virtualId}
                      addons={addons}
                      maxQuantity={maxQuantity}
                      selectedAddon={selectedAddon}
                      onSelectAddon={handleSelectAddon}
                      onUnselectAddon={handleUnselectAddon}
                      onAddAddon={handleAddAddon}
                      onRemoveAddon={handleRemoveAddon}
                    />
                </Element>
            </div>
        </div>
    );
};

AddonsConfigurator.propTypes = {
    addons: PropTypes.arrayOf(PropTypes.shape({
        addonGroups: PropTypes.arrayOf(PropTypes.object),
        id: PropTypes.string.isRequired,
        maxQuantity: PropTypes.number.isRequired,
        minQuantity: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        parentProductId: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        controlType: PropTypes.oneOf(['radio', 'checkbox', 'multi', 'configurable']).isRequired,
        productId: PropTypes.string.isRequired
    })),
    controlType: PropTypes.oneOf(['radio', 'checkbox', 'multi', 'configurable']).isRequired,
    id: PropTypes.string.isRequired,
    virtualId: PropTypes.string.isRequired,
    selectedAddon: PropTypes.string,
    name: PropTypes.string.isRequired,
    setAddonAsSelected: PropTypes.func.isRequired,
    setAddonAsUnselected: PropTypes.func.isRequired,
    addAddon: PropTypes.func.isRequired,
    removeAddon: PropTypes.func.isRequired,
    validationErrors: PropTypes.arrayOf(PropTypes.object),
    maxQuantity: PropTypes.number.isRequired,
    selectTranslations: PropTypes.object
};

AddonsConfigurator.defaultProps = {
    validationErrors: [],
    selectTranslations: {
        'radio': 'selectOptions',
        'checkbox': 'selectMaxOptions',
        'multi': 'selectMaxOptions',
        'configurable': 'selectMaxOptions'
    }
};

export default AddonsConfigurator;
